import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../../i18n/LanguageContext';
import { deviceHeader } from '../../utils/deviceId';

// Hide docker/internal/private IPs so users don't see scary-looking 172.18.0.1
// on every session and think the account was accessed from an unknown place.
// Backend still needs the nginx X-Real-IP fix to store the true client IP.
const isPrivateOrInternalIP = (ip) => {
  if (!ip || typeof ip !== 'string') return true;
  const v = ip.trim().toLowerCase();
  if (!v || v === 'unknown' || v === 'null' || v === '::1') return true;
  if (v.startsWith('127.') || v.startsWith('10.') || v.startsWith('192.168.')) return true;
  if (v.startsWith('169.254.')) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(v)) return true;
  if (v.startsWith('fc') || v.startsWith('fd') || v.startsWith('fe80')) return true;
  return false;
};

/**
 * DevicesCard
 * ─────────────
 * Shows the user's active devices + recent sign-in events. Lets them sign out
 * any device or all-other-devices with a confirmation popup.
 *
 * Used in UserProfile (after Weather Settings).
 *
 * Props:
 *   - API: backend base URL
 *   - userToken: JWT for the current user
 *   - isDarkMode: theme flag
 *   - showNotification(msg, type?): optional toast helper passed by parent
 */
export default function DevicesCard({ API, userToken, isDarkMode = false, showNotification }) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [deviceLimit, setDeviceLimit] = useState(3);
  const [currentDeviceId, setCurrentDeviceId] = useState('');
  const [confirmTarget, setConfirmTarget] = useState(null); // { device } OR { all: true }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await fetch(`${API}/auth/sessions`, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
          ...deviceHeader(),
        },
      });
      if (!r.ok) return;
      const data = await r.json();
      setDevices(data.devices || []);
      setRecentEvents(data.recent_events || []);
      setDeviceLimit(data.device_limit || 3);
      setCurrentDeviceId(data.current_device_id || '');
    } catch (e) {
      // noop — silent fail; user sees stale data
    } finally {
      setLoading(false);
    }
  }, [API, userToken]);

  useEffect(() => {
    if (userToken) load();
  }, [userToken, load]);

  const handleSignOut = async () => {
    if (!confirmTarget) return;
    setBusy(true);
    try {
      let endpoint;
      let label;
      if (confirmTarget.all) {
        endpoint = `${API}/auth/sessions/all-others`;
        label = t('sign_out_all_other_devices') || 'Sign out all other devices';
      } else {
        endpoint = `${API}/auth/sessions/${confirmTarget.device.device_id}`;
        label = `${t('signed_out') || 'Signed out'} · ${confirmTarget.device.device_label}`;
      }
      const r = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          ...deviceHeader(),
        },
      });
      if (r.ok) {
        if (showNotification) showNotification(label, 'success');
        setConfirmTarget(null);
        await load();
      } else {
        if (showNotification) showNotification(t('error_network') || 'Network error', 'error');
      }
    } catch (e) {
      if (showNotification) showNotification(t('error_network') || 'Network error', 'error');
    } finally {
      setBusy(false);
    }
  };

  const deviceIcon = (type) => {
    if (type === 'phone') return '📱';
    if (type === 'tablet') return '📱';
    return '💻';
  };

  // Used for the ACTIVE DEVICES section — small relative times like "Active now" / "5 min ago"
  const formatTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
      if (diffMin < 1) return t('active_now') || 'Active now';
      if (diffMin < 60) return `${diffMin} ${t('min_ago') || 'min ago'}`;
      const diffHr = Math.round(diffMin / 60);
      if (diffHr < 24) return `${diffHr} ${t('hours_ago') || 'h ago'}`;
      const diffDay = Math.round(diffHr / 24);
      return `${diffDay} ${t('days_ago') || 'd ago'}`;
    } catch {
      return '';
    }
  };

  // Used for the RECENT SIGN-INS history — actual date + time so users can audit "what happened when"
  const formatEventTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const today = new Date();
      const sameDay = d.toDateString() === today.toDateString();
      const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      if (sameDay) return `${t('today') || 'Today'} ${time}`;
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      if (d.toDateString() === yesterday.toDateString()) return `${t('yesterday') || 'Yesterday'} ${time}`;
      return `${d.toLocaleDateString()} ${time}`;
    } catch {
      return '';
    }
  };

  const eventLabel = (ev) => {
    const map = {
      signin: t('event_signin') || 'Signed in',
      revoked: t('event_revoked') || 'Signed out',
      kicked_by_limit: t('event_kicked_by_limit') || 'Replaced by new device',
      expired: t('event_expired') || 'Expired',
      admin_logout: t('event_admin_logout') || 'Signed out by admin',
    };
    return map[ev.event] || ev.event;
  };

  const otherDevicesCount = devices.filter(d => !d.is_current).length;

  return (
    <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'} mt-6`}>
      <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        📱 {t('devices_and_activity') || 'Devices & Activity'}
      </h3>
      <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {t('devices_subtitle') || 'Sign out any device you don\'t recognize. We\'ll let you know if a new device signs in.'}
      </p>

      {/* Active devices */}
      <div className="mb-4">
        {/* While loading, hide the count/list so the user doesn't see "(0 / 3)" → "(1 / 3)" flicker */}
        {loading ? (
          <div className={`text-sm py-2 flex items-center gap-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
            {t('loading') || 'Loading...'}
          </div>
        ) : (
        <>
        <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {t('active_devices') || 'Active devices'} ({devices.length} / {deviceLimit})
        </div>
        {!loading && devices.length === 0 && (
          <div className={`text-sm p-4 rounded-lg ${isDarkMode ? 'bg-amber-900/20 border border-amber-700/40 text-amber-200' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}>
            <div className="font-semibold mb-1">⚠️ No active devices yet</div>
            <div className="text-xs leading-relaxed">
              If you're signed in but see no device here, your sign-in happened before device tracking was enabled.
              <br /><strong>Sign out and sign back in once</strong> to register this device. It'll show up here after that.
            </div>
          </div>
        )}
        <div className="space-y-2">
          {devices.map(d => (
            <div
              key={d.device_id}
              className={`flex items-center gap-3 p-3 rounded-xl border ${
                d.is_current
                  ? (isDarkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-300')
                  : (isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200')
              }`}
            >
              <span className="text-2xl">{deviceIcon(d.device_type)}</span>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-semibold flex items-center gap-2 flex-wrap ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  <span className="truncate">{d.device_label}</span>
                  {d.is_current && (
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>
                      {t('this_device') || 'This device'}
                    </span>
                  )}
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {d.ip && !isPrivateOrInternalIP(d.ip) && <span className="mr-2">{d.ip}</span>}
                  <span>{formatTime(d.last_active)}</span>
                </div>
              </div>
              {!d.is_current && (
                <button
                  type="button"
                  onClick={() => setConfirmTarget({ device: d })}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${isDarkMode ? 'bg-red-900/30 text-red-300 hover:bg-red-900/50' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                >
                  {t('sign_out') || 'Sign out'}
                </button>
              )}
            </div>
          ))}
        </div>
        {otherDevicesCount > 0 && (
          <button
            type="button"
            onClick={() => setConfirmTarget({ all: true })}
            className={`mt-3 w-full text-sm font-semibold py-2 rounded-xl transition-colors ${isDarkMode ? 'bg-gray-800 border border-gray-700 text-red-300 hover:bg-gray-700' : 'bg-red-50 border border-red-200 text-red-600 hover:bg-red-100'}`}
          >
            {t('sign_out_all_other_devices') || 'Sign out all other devices'}
          </button>
        )}
        </>
        )}
      </div>

      {/* Recent events */}
      {recentEvents.length > 0 && (
        <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-600">
          <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {t('recent_sign_ins') || 'Recent sign-ins'} ({t('last_10') || 'last 10'})
          </div>
          <ul className="space-y-1.5">
            {recentEvents.map((ev, i) => (
              <li key={i} className={`text-xs flex items-center gap-2 flex-wrap ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <span>🕒</span>
                <span className="font-medium">{eventLabel(ev)}</span>
                {ev.device_label && <span className="opacity-70">· {ev.device_label}</span>}
                {ev.ip && !isPrivateOrInternalIP(ev.ip) && <span className="opacity-50">· {ev.ip}</span>}
                <span className="opacity-60 ml-auto font-mono text-[11px]">{formatEventTime(ev.timestamp)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Confirmation modal */}
      {confirmTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className={`w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden ${isDarkMode ? 'bg-gray-900 ring-1 ring-gray-700' : 'bg-white ring-1 ring-gray-200'}`}>
            <div className={`px-5 py-4 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              <h3 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {confirmTarget.all
                  ? `${t('sign_out_count_devices') || 'Sign out'} ${otherDevicesCount} ${otherDevicesCount === 1 ? (t('device') || 'device') : (t('devices') || 'devices')}?`
                  : (t('sign_out_this_device') || 'Sign out this device?')
                }
              </h3>
            </div>
            <div className="p-5">
              {confirmTarget.device && (
                <div className={`flex items-center gap-3 p-3 rounded-xl mb-3 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <span className="text-2xl">{deviceIcon(confirmTarget.device.device_type)}</span>
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{confirmTarget.device.device_label}</div>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{formatTime(confirmTarget.device.last_active)}</div>
                  </div>
                </div>
              )}
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {confirmTarget.all
                  ? (t('confirm_sign_out_all') || 'All other devices will be signed out immediately. Your current device stays signed in.')
                  : (t('confirm_sign_out_one') || 'The person using this device will be signed out immediately. They\'ll see a message explaining why.')
                }
              </p>
            </div>
            <div className={`px-5 py-4 border-t flex items-center justify-end gap-2 ${isDarkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-100 bg-gray-50'}`}>
              <button
                type="button"
                onClick={() => setConfirmTarget(null)}
                disabled={busy}
                className={`px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ${isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                {t('cancel') || 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleSignOut}
                disabled={busy}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {busy ? (t('signing_out') || 'Signing out...') : (confirmTarget.all ? (t('sign_out_devices_button') || 'Sign out devices') : (t('sign_out_device_button') || 'Sign out device'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
